// Queries de CAMADA.
//
// Decisión 2 del ADR-022: la camada activa es la única con
// cerrada = false. El backend expone GET /api/v1/camadas con filtros
// (limit, cerrada, codigo_lote). La activa se consulta por separado;
// el historial usa páginas de 50 camadas.

import { apiFetch, qk, useApiMutation, useApiQuery } from './client';
import type { Camada, CamadaCreateInput, Page } from '@/lib/types';
import type { CamadaDetalle } from '@/lib/types';

const CAMADAS_KEY = qk('camadas');

/** Lista camadas activas (cerrada = false). */
export function useCamadasActivas() {
  return useApiQuery<Page<Camada>>({
    queryKey: [...CAMADAS_KEY, 'activas'],
    queryFn: () => apiFetch<Page<Camada>>('/v1/camadas?cerrada=false&limit=50'),
    // El estado de la camada activa cambia en commit (crear/cerrar).
    // 30s es suficiente para que dos pantallas coordinen.
    staleTime: 30 * 1000,
  });
}

/** Detalle de una camada por id. */
export function useCamada(id: number | null) {
  return useApiQuery<Camada>({
    queryKey: id != null ? [...CAMADAS_KEY, id] : qk('camadas', 'detail', 'none'),
    queryFn: () => apiFetch<Camada>(`/v1/camadas/${id}`),
    enabled: id != null,
    staleTime: 30 * 1000,
  });
}

/**
 * Camada activa = la única con cerrada=false.
 * Devuelve la primera; en la práctica la BD no debería tener más de una
 * (decisión 2 del ADR-022), pero si por algún motivo hay varias,
 * mostramos la más reciente por id.
 */
export function useCamadaActiva() {
  const query = useCamadasActivas();
  const camada = query.data?.items[0] ?? null;
  return {
    ...query,
    data: camada,
  };
}

export function useCrearCamada() {
  return useApiMutation<CamadaCreateInput, Camada>({
    mutationFn: (body, context) =>
      apiFetch<Camada>('/v1/camadas', {
        offlineBase: context.offlineBase,
        method: 'POST',
        body: JSON.stringify(body),
      }),
    invalidateKeys: [CAMADAS_KEY, ['liquidacion-evolucion']],
  });
}

// ---- M5 (v0.5 mantenimiento de camadas) ----------------------------------

/** Filtros del listado de CamadasPage. */
export interface CamadasListadoFiltros {
  year?: number;
  month?: number;
  sexo?: 'machos' | 'hembras';
  order?: 'fecha_entrada_desc' | 'fecha_cierre_desc';
  offset?: number;
}

/**
 * Lista camadas con los filtros de M5 (D3: Año + Mes + Sexo).
 * Devuelve Page<Camada> con el total para que la UI muestre el
 * subtítulo "X camadas en total".
 */
export function useCamadasListado(filtros: CamadasListadoFiltros = {}) {
  const params = new URLSearchParams();
  if (filtros.year != null) params.set('year', String(filtros.year));
  if (filtros.month != null) params.set('month', String(filtros.month));
  if (filtros.sexo) params.set('sexo', filtros.sexo);
  if (filtros.order) params.set('order', filtros.order);
  // Páginas de 50; el selector permite recorrer también el historial completo.
  params.set('limit', '50');
  if (filtros.offset) params.set('offset', String(filtros.offset));
  const qs = params.toString();

  return useApiQuery<Page<Camada>>({
    queryKey: [
      ...CAMADAS_KEY,
      'listado',
      filtros.year ?? 'all',
      filtros.month ?? 'all',
      filtros.sexo ?? 'all',
      filtros.order ?? 'fecha_entrada_desc',
      ...(filtros.offset ? [filtros.offset] : []),
    ],
    queryFn: () => apiFetch<Page<Camada>>(`/v1/camadas?${qs}`),
    staleTime: 30 * 1000,
  });
}

/** Detalle completo de una camada (M5). */
export function useCamadaDetalle(id: number | null) {
  return useApiQuery<CamadaDetalle>({
    queryKey: id != null ? [...CAMADAS_KEY, id, 'detalle'] : qk('camadas', 'detalle', 'none'),
    queryFn: () => apiFetch<CamadaDetalle>(`/v1/camadas/${id}/detalle`),
    enabled: id != null,
    staleTime: 30 * 1000,
  });
}

/** Cerrar camada (M5). El id se pasa como argumento al mutar. */
export function useCerrarCamada() {
  return useApiMutation<
    {
      id: number;
      motivo_justificacion_descuadre?: string | null;
      kg_sobrante_pienso?: number | string | null;
    },
    Camada
  >({
    mutationFn: ({ id, motivo_justificacion_descuadre, kg_sobrante_pienso }, context) =>
      apiFetch<Camada>(`/v1/camadas/${id}/cerrar`, {
        offlineBase: context.offlineBase,
        method: 'POST',
        body: JSON.stringify({
          motivo_justificacion_descuadre: motivo_justificacion_descuadre ?? null,
          kg_sobrante_pienso: kg_sobrante_pienso ?? null,
        }),
      }),
    invalidateKeys: [['camadas'], ['liquidacion'], ['entregas-pienso'], ['liquidacion-evolucion']],
  });
}

/** Reabrir camada (M5). */
export function useReabrirCamada() {
  return useApiMutation<{ id: number }, Camada>({
    mutationFn: ({ id }, context) =>
      apiFetch<Camada>(`/v1/camadas/${id}/reabrir`, {
        offlineBase: context.offlineBase,
        method: 'POST',
      }),
    invalidateKeys: [['camadas'], ['liquidacion'], ['entregas-pienso'], ['liquidacion-evolucion']],
  });
}

/** Editar campos editables de la camada (M5, D5 revisada). */
export function useEditarCamada() {
  return useApiMutation<{ id: number; data: Partial<CamadaCreateInput> }, Camada>({
    mutationFn: ({ id, data }, context) =>
      apiFetch<Camada>(`/v1/camadas/${id}`, {
        offlineBase: context.offlineBase,
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    invalidateKeys: [['liquidacion-evolucion']],
  });
}

/** Eliminar una camada abierta sin dependencias ni liquidación guardada. */
export function useEliminarCamada() {
  return useApiMutation<{ id: number }, void>({
    mutationFn: ({ id }, context) =>
      apiFetch<void>(`/v1/camadas/${id}`, {
        offlineBase: context.offlineBase,
        method: 'DELETE',
      }),
    invalidateKeys: [['liquidacion-evolucion']],
  });
}
